import { useEffect, useMemo } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useLocation, useParams } from 'react-router-dom';

import { Box, Grid, Stack } from '@mui/material';

import { CfsApi } from '@/api';
import { Page, UnitShiftList } from '@/components';
import { careLinkTab, cfsTabs } from '@/constants';
import { CFSProvider, UnitShiftProvider } from '@/contexts';
import {
  useCFSContext,
  useCFSNavigation,
  useSyncCFSNavigation,
  useUnitShiftContext,
} from '@/hooks';
import { BottomTabBar, TabLayout } from '@/layouts/TabLayout';
import { ICFSForm, IUser, Role } from '@/models';
import { isTargetRole } from '@/services';
import {
  useCallerCommunicationStore,
  useCFSNavigationStore,
  useUserStore,
} from '@/store';
import { getMasterNamePhoneNumbersByPriority, parsePhoneNumber } from '@/utils';

import { useCFSCallerBadges } from '../CFSCallerCommunication/hooks/useCFSCallerBadges';
import RadioClicks from '../RadioClicks/RadioClicks';
import { CFSForm } from './components/CFSForm';
import CFSHeader from './components/CFSHeader';
import { CFSMap } from './components/CFSMap';
import { LiveInformation } from './components/LiveInformation';
import { cfsDefaultValues } from './data';
import { CFSEditRoot } from './styles';

const CFSEdit = (props: { cfsId?: string; isQuickView?: boolean }) => {
  const urlParams = useParams();
  const cfsId = props?.cfsId || String(urlParams.cfsId);
  const { cfs } = useCFSContext();
  const { setBackRoute, backRoute } = useUnitShiftContext();
  const { user, account } = useUserStore();
  useCFSCallerBadges(cfsId);
  const callerUnreadTotal = useCallerCommunicationStore((s) =>
    s.getTotalUnreadCount(),
  );
  const callerNewLocationTotal = useCallerCommunicationStore((s) =>
    s.getTotalNewLocationCount(),
  );
  const callerCommCombinedTotal = callerUnreadTotal + callerNewLocationTotal;
  const callerCommIconClass =
    callerUnreadTotal > 0 && callerNewLocationTotal > 0
      ? 'meta_icon--both'
      : callerNewLocationTotal > 0
      ? 'meta_icon--location'
      : callerUnreadTotal > 0
      ? 'meta_icon--message'
      : undefined;
  const { goToNext, goToPrevious } = useCFSNavigation(cfsId);
  useSyncCFSNavigation(cfsId);
  const isEnabledRadioClicksModule =
    account?.settings?.features?.isEnabledRadioClicksModule;

  const isEnabledNursingHome =
    account?.settings?.features?.isEnabledNursingHome;

  const logsTabIndex = cfsTabs.findIndex(({ path }) => path === 'history');
  const cfsTabLinks = useMemo(() => {
    // Hide "Caller communication" tab on closed CFS (feature only applies to active incidents)
    const baseTabs = cfs?.isClosed
      ? cfsTabs.filter((t) => t.path !== 'caller-communication')
      : cfsTabs;

    if (!isEnabledNursingHome) {
      return baseTabs;
    }

    const logsIdx = baseTabs.findIndex(({ path }) => path === 'history');
    if (logsIdx < 0) {
      return [...baseTabs, careLinkTab];
    }

    return [
      ...baseTabs.slice(0, logsIdx),
      careLinkTab,
      ...baseTabs.slice(logsIdx),
    ];
  }, [isEnabledNursingHome, logsTabIndex, cfs?.isClosed]);

  const { pathname } = useLocation();
  const basePath = useMemo(() => {
    const segments = pathname.split('/').filter(Boolean);
    const cfsIdIndex = cfsId ? segments.indexOf(cfsId) : -1;

    // Prefer a basePath that includes the cfsId segment:
    // - /cfs/:cfsId
    // - /cfs/slide/:cfsId
    // - /cfs-active/:cfsId
    if (cfsIdIndex >= 0) {
      return `/${segments.slice(0, cfsIdIndex + 1).join('/')}`;
    }

    // Fallback to the previous behavior (best-effort).
    if (segments.length >= 2) {
      return `/${segments[0]}/${segments[1]}`;
    }
    return segments.length === 1 ? `/${segments[0]}` : '/';
  }, [pathname, cfsId]);

  const formMethods = useForm<ICFSForm>({
    defaultValues: cfsDefaultValues,
    mode: 'onChange',
  });

  // Set default back route if none exists
  useEffect(() => {
    if (setBackRoute && !backRoute) {
      // Add a small delay to ensure context has been properly initialized
      const timer = setTimeout(() => {
        // Double-check that no back route was set during initialization
        if (!backRoute) {
          // Default based on the current route context
          setBackRoute(
            pathname.startsWith('/cfs-active') ? '/cfs-active' : '/cfs',
          );
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [setBackRoute, backRoute, pathname]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey)) {
        return;
      }
      if (e.code === 'Comma' || (e.shiftKey && e.key === '<')) {
        e.preventDefault();
        goToNext();
      }

      if (e.code === 'Period' || (e.shiftKey && e.key === '>')) {
        e.preventDefault();
        goToPrevious();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [goToNext, goToPrevious]);

  useEffect(() => {
    if (cfs?._id) {
      const { primaryPhoneNumber } = getMasterNamePhoneNumbersByPriority(
        cfs.primaryReporter,
      );
      const data: ICFSForm = {
        number: cfs.number,
        severity: cfs.severity,
        shortDescription: cfs.shortDescription,
        incidentCodes: (cfs.incidentCodes || []).map((code) =>
          String(code._id),
        ),
        requiredUnits: cfs.requiredUnits || [],
        assignedAgencies: cfs.assignedAgencies,
        addressInfo: {
          ...cfsDefaultValues.addressInfo,
          ...cfs.addressInfo,
          // Carry the per-CFS business name onto the form's address value so
          // the Address input renders "Business - Street" after load.
          address: cfs.addressInfo?.address
            ? {
                ...cfs.addressInfo.address,
                businessName:
                  cfs.addressInfo.businessName ??
                  cfs.addressInfo.address.businessName,
              }
            : cfs.addressInfo?.address,
          instructions: cfs.addressInfo?.instructions || '',
        },
        reporter: {
          ...cfs.reporter,
          contactPhoneNumber:
            primaryPhoneNumber?.inputValue ||
            parsePhoneNumber(cfs.reporter.contactPhoneNumber),
        },
      };
      formMethods.reset(data);
    }
  }, [cfs?._id]);

  // Populate navigation list for new window scenarios or ensure current CFS is in list
  useEffect(() => {
    if (!cfsId) return;

    const store = useCFSNavigationStore.getState();
    const currentSourceRoute = window.location.pathname.startsWith(
      '/cfs-active',
    )
      ? 'cfs-active'
      : window.location.pathname.startsWith('/cfs')
      ? 'cfs'
      : null;

    // If store already has data with current CFS, nothing to do
    if (
      store.cfsNavigationList.length > 0 &&
      store.cfsNavigationList.includes(cfsId) &&
      (!currentSourceRoute ||
        store.navigationContext?.sourceRoute === currentSourceRoute)
    ) {
      return;
    }

    // Try to restore from localStorage first (handles new window scenarios)
    const stored = localStorage.getItem('cfs-navigation');
    if (stored) {
      try {
        const { ids, sourceRoute, navigationContext } = JSON.parse(stored);
        if (Array.isArray(ids) && ids.length) {
          // Ensure current cfsId is in the list (prepend if not found)
          const finalIds = ids.includes(cfsId) ? ids : [cfsId, ...ids];
          useCFSNavigationStore.getState().setCFSNavigationList(
            finalIds.map((id: string) => ({ _id: id })),
            sourceRoute || 'cfs',
            navigationContext,
          );
          return; // Successfully restored from localStorage
        }
      } catch {
        // Invalid JSON in localStorage, continue to fallback
      }
    }

    // Fallback: If URL is /cfs-active and no localStorage, fetch fresh list
    const isCfsActive = window.location.pathname.startsWith('/cfs-active');
    if (isCfsActive) {
      const requestParams = { sort: '{"createdAt": -1}' };
      CfsApi.activeList({ sort: '{"createdAt": -1}', limit: 100 })
        .then((response) => {
          const responseIds = response.data
            .map((item: { _id?: string }) => item._id)
            .filter(Boolean);
          const finalData = responseIds.includes(cfsId)
            ? response.data
            : [{ _id: cfsId }, ...response.data];
          useCFSNavigationStore
            .getState()
            .setCFSNavigationList(finalData, 'cfs-active', {
              sourceRoute: 'cfs-active',
              requestParams,
              userFilter: {},
            });
        })
        .catch(() => {});
    }
  }, [cfsId]);

  return (
    <Page
      title={cfs ? `${cfs?.number} detail page` : 'Loading ...'}
      style={{ height: '100vh' }}
    >
      <CFSEditRoot>
        <FormProvider {...formMethods}>
          <Grid columnSpacing={2} container>
            <Grid item xs={12} lg={8}>
              <Stack justifyContent="space-between" rowGap={2.5}>
                <CFSHeader
                  cfsId={cfsId}
                  isQuickView={props.isQuickView}
                  accountSettings={account?.settings}
                  enableActiveUsers={
                    isTargetRole(user as IUser, Role.DISPATCHER) &&
                    !cfs?.isClosed
                  }
                />

                <Stack className="active-cfs-content">
                  <Grid
                    spacing={2}
                    container
                    sx={{ flex: 1, overflow: 'hidden' }}
                  >
                    <Grid
                      item
                      xs={12}
                      lg={6}
                      sx={{
                        maxHeight: 'calc(100vh - 180px)',
                        overflow: 'auto',
                      }}
                    >
                      <CFSForm cfsId={cfsId} />
                    </Grid>

                    <Grid
                      item
                      xs={12}
                      lg={6}
                      className="active-cfs-widgets"
                      sx={{
                        maxHeight: 'calc(100vh - 180px)',
                        minHeight: 0,
                        overflowY: 'auto',
                      }}
                    >
                      <Stack spacing={2} sx={{ height: '100%' }}>
                        <CFSMap cfsId={cfsId} />
                        <Box
                          sx={{
                            minHeight: 300,
                            flex: 1,
                            display: 'flex',
                            overflow: 'hidden',
                          }}
                        >
                          <LiveInformation
                            cfsId={cfsId}
                            maxChars={1500}
                            isMaxHeight
                          />
                        </Box>
                      </Stack>
                    </Grid>
                  </Grid>
                </Stack>
              </Stack>
            </Grid>

            <Grid item xs={12} lg={4} className="mapbox-unit-list">
              <Stack className="unit-list">
                <UnitShiftList cfsId={cfsId} />
              </Stack>
            </Grid>
          </Grid>
          <TabLayout
            tabLinks={cfsTabLinks}
            basePath={basePath}
            style={{ width: 'calc(100% - 40px)', position: 'absolute' }}
          >
            <BottomTabBar
              additionalInfoByPath={{
                names: {
                  showWarning:
                    !!cfs?.hasUnsetInvolvementName && (cfs?.names ?? 0) > 0,
                  count: cfs?.names,
                },
                vehicles: { count: cfs?.vehicles },
                properties: { count: cfs?.properties },
                narratives: { count: cfs?.narrativesCount },
                media: { count: cfs?.files?.length },
                'files-and-forms': {
                  count:
                    (cfs as any)?.domesticIncidentReports?.filter(
                      (d: any) => !d.deleted,
                    )?.length ?? 0,
                },
                'related-records': { count: cfs?.relatedRecordsCount },
                'caller-communication': {
                  count: callerCommCombinedTotal,
                  iconClass: callerCommIconClass,
                },
              }}
              isQuickView={props.isQuickView}
            />
          </TabLayout>
          {isEnabledRadioClicksModule && (
            <Grid columnSpacing={2} container>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: 0,
                  display: 'flex',
                  flex: 1,
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  paddingX: 2,
                }}
              >
                <RadioClicks />
              </Box>
            </Grid>
          )}
        </FormProvider>
      </CFSEditRoot>
    </Page>
  );
};

const ActiveCFSDetail = (props: { cfsId?: string; isQuickView?: boolean }) => {
  const urlParams = useParams();
  const cfsId = props?.cfsId || String(urlParams.cfsId);

  if (!cfsId) return null;

  return (
    <CFSProvider cfsId={cfsId}>
      <UnitShiftProvider>
        <CFSEdit cfsId={cfsId} isQuickView={props.isQuickView} />
      </UnitShiftProvider>
    </CFSProvider>
  );
};

export default ActiveCFSDetail;
